/**
 * ONDE OS DADOS MORAM NO APARELHO — e o que ainda falta enviar ao banco.
 *
 * Tudo passa por `mudar()`. Ela grava no localStorage e, sozinha, marca como
 * PENDENTE toda ficha ou produtor que mudou (compara a hora da alteração antes
 * e depois). Nenhuma tela precisa lembrar de "avisar a fila": esquecer era o
 * erro mais provável. O que vem do servidor entra com `doServidor`, e aí não
 * vira pendência.
 *
 * DADOS POR PESSOA: a chave do armazenamento leva o id de quem entrou. Duas
 * pessoas no mesmo celular não veem nem misturam as fichas uma da outra.
 * Fichas feitas antes do login (etapa 1) são ADOTADAS pela primeira pessoa
 * que entrar no aparelho, e sobem no primeiro «Sincronizar».
 *
 * Ao mudar o formato dos dados guardados, mudar a versão da CHAVE — e avisar.
 */

import { useSyncExternalStore } from 'react'
import { agora, hoje, normalizar, novoId } from '../dominio/numeros'
import type { Ajustes, Ficha, Produtor } from '../dominio/tipos'
import type { Divergencia } from '../dominio/juntar'

const CHAVE_ANTIGA = 'pesagem.v1'
/** cada pessoa tem a sua: `pesagem.v1.<id da pessoa>` (a 3E lê as dos outros para oferecer a adoção) */
export const PREFIXO_DA_PESSOA = 'pesagem.v1.'
const prefixo = PREFIXO_DA_PESSOA

export interface Conflito {
  /** a versão que estava neste aparelho quando o servidor respondeu */
  local: Ficha
  em: string
  /**
   * R1A · o que ficou diferente depois de juntar as duas versões (as pesagens
   * já foram juntadas e estão valendo). Ausente em conflito antigo ou da
   * adoção: a junção é feita na hora de resolver (src/dados/fila.ts).
   */
  divergencias?: Divergencia[]
}

export interface Estado {
  fichas: Ficha[]
  produtores: Produtor[]
  ajustes: Ajustes
  /** último número usado, por ano (o número leva a sigla da pessoa na frente) */
  seq: Record<string, number>
  /** o que mudou aqui e ainda não subiu */
  pendentes: { fichas: string[]; produtores: string[] }
  /** hora do servidor na última sincronização (para pedir só o que é novo) */
  ultimaSync: string | null
  /** fichas que outro aparelho mudou antes: as duas versões ficam à vista */
  conflitos: Conflito[]
  /**
   * 3E · O servidor recusou de vez (4xx que não é conflito nem espera): sai
   * da fila e fica aqui, com a frase do servidor — nada se apaga. Mexer de
   * novo na ficha ou no produtor o põe na fila outra vez; subindo, sai daqui.
   */
  recusadas: { fichas: Record<string, string>; produtores: Record<string, string> }
  /**
   * 3E · De quem são estes dados (gravado ao abrir). Serve para outra pessoa
   * da mesma empresa — o gestor — saber de quem são as fichas presas neste
   * aparelho. Ausente em dados gravados antes da 3E.
   */
  de?: Dono
}

export const CATEGORIAS_PADRAO = ['Boi', 'Vaca', 'Novilha', 'Garrote', 'Bezerro', 'Bezerra', 'Touro']

function vazio(): Estado {
  return {
    fichas: [],
    produtores: [],
    seq: {},
    pendentes: { fichas: [], produtores: [] },
    ultimaSync: null,
    conflitos: [],
    recusadas: { fichas: {}, produtores: {} },
    ajustes: {
      emitente: { nome: '', doc: '', telefone: '', cidade: '' },
      categorias: CATEGORIAS_PADRAO.slice(),
      base: 'arroba',
      rendimento: 50,
      financeiroPct: 0,
      funruralPct: 0,
      tema: 'auto',
      ultimaCopia: null,
    },
  }
}

function completar(e: Partial<Estado> | null): Estado {
  const v = vazio()
  if (!e || typeof e !== 'object') return v
  return {
    fichas: Array.isArray(e.fichas) ? e.fichas : [],
    produtores: Array.isArray(e.produtores) ? e.produtores : [],
    seq: e.seq ?? {},
    pendentes: {
      fichas: Array.isArray(e.pendentes?.fichas) ? e.pendentes.fichas : [],
      produtores: Array.isArray(e.pendentes?.produtores) ? e.pendentes.produtores : [],
    },
    ultimaSync: e.ultimaSync ?? null,
    conflitos: Array.isArray(e.conflitos) ? e.conflitos : [],
    recusadas: {
      fichas: objeto(e.recusadas?.fichas),
      produtores: objeto(e.recusadas?.produtores),
    },
    ...(e.de && typeof e.de === 'object' ? { de: e.de } : {}),
    ajustes: { ...v.ajustes, ...(e.ajustes ?? {}), emitente: { ...v.ajustes.emitente, ...(e.ajustes?.emitente ?? {}) } },
  }
}

function objeto(x: unknown): Record<string, string> {
  return x && typeof x === 'object' && !Array.isArray(x) ? { ...(x as Record<string, string>) } : {}
}

/** Lê e completa os dados guardados numa chave qualquer (os de outra pessoa, na adoção). */
export function lerDados(t: string | null): Estado {
  return completar(t ? JSON.parse(t) : null)
}

/** true quando o navegador recusou gravar: a tela avisa que os dados vão sumir. */
export let semDisco = false

/** Quem é o dono dos dados abertos agora (definido ao entrar). */
/** R1A: `papel` gravado nos dados — a adoção oferece as fichas de quem não grava (Consulta). */
export interface Dono { id: string; nome: string; sigla: string; contaId?: string; papel?: string }
let dono: Dono | null = null
let chave = CHAVE_ANTIGA

export function lerDono(): Dono | null {
  return dono
}

function carregar(): Estado {
  try {
    const t = localStorage.getItem(chave)
    return completar(t ? JSON.parse(t) : null)
  } catch {
    semDisco = true
    return vazio()
  }
}

let estado = vazio()
const ouvintes = new Set<() => void>()

/**
 * Abre os dados da pessoa que entrou. Na primeira vez neste aparelho, adota o
 * que foi feito antes do login (chave antiga), marcando tudo como pendente.
 */
export function abrirDoDono(d: Dono): void {
  if (dono?.id === d.id) { dono = d; return }
  dono = d
  chave = prefixo + d.id
  try {
    const antigo = localStorage.getItem(CHAVE_ANTIGA)
    if (antigo && !localStorage.getItem(chave)) {
      const e = completar(JSON.parse(antigo))
      e.pendentes = { fichas: e.fichas.map((f) => f.id), produtores: e.produtores.map((p) => p.id) }
      localStorage.setItem(chave, JSON.stringify(e))
      localStorage.removeItem(CHAVE_ANTIGA)
    }
  } catch { /* sem disco: segue com o que der */ }
  estado = carregar()
  // 3E: os dados dizem de quem são (e de que empresa) — para a adoção pelo gestor
  if (estado.de?.id !== d.id || estado.de?.nome !== d.nome || estado.de?.sigla !== d.sigla || (d.contaId && estado.de?.contaId !== d.contaId)
      || (d.papel && estado.de?.papel !== d.papel)) {
    estado = { ...estado, de: { ...d } }
    try { localStorage.setItem(chave, JSON.stringify(estado)) } catch { semDisco = true }
  }
  ouvintes.forEach((f) => f())
}

export function usarEstado(): Estado {
  return useSyncExternalStore(
    (f) => { ouvintes.add(f); return () => ouvintes.delete(f) },
    () => estado,
  )
}

export function lerEstado(): Estado {
  return estado
}

function marcarPendentes(antes: Estado, depois: Estado): void {
  const fAntes = new Map(antes.fichas.map((f) => [f.id, f.alteradaEm]))
  for (const f of depois.fichas) {
    if (fAntes.get(f.id) !== f.alteradaEm && !depois.pendentes.fichas.includes(f.id)) depois.pendentes.fichas.push(f.id)
  }
  const pAntes = new Map(antes.produtores.map((p) => [p.id, p.alteradoEm]))
  for (const p of depois.produtores) {
    if (pAntes.get(p.id) !== p.alteradoEm && !depois.pendentes.produtores.includes(p.id)) depois.pendentes.produtores.push(p.id)
  }
}

/** Toda alteração passa por aqui: trabalha numa cópia, marca pendências, grava, avisa as telas. */
export function mudar(fn: (e: Estado) => void, opcoes?: { doServidor?: boolean }): void {
  const novo = structuredClone(estado)
  fn(novo)
  if (!opcoes?.doServidor) marcarPendentes(estado, novo)
  estado = novo
  try {
    localStorage.setItem(chave, JSON.stringify(estado))
    semDisco = false
  } catch {
    semDisco = true
  }
  ouvintes.forEach((f) => f())
}

/**
 * R1A · «Sair»: das fichas e produtores da pessoa ficam só os que o banco
 * ainda não tem — pendentes, recusados, em conflito ou que nunca subiram (as
 * fichas presas ficam para o gestor poder trazê-las). O resto já está no
 * banco e volta inteiro no próximo «Sincronizar» (a última sincronia é
 * esquecida de propósito).
 */
export function enxugarAoSair(): void {
  if (!dono) return
  const e = estado
  const emConflito = new Set(e.conflitos.map((c) => c.local.id))
  const fichas = e.fichas.filter((f) => e.pendentes.fichas.includes(f.id) || f.id in e.recusadas.fichas
    || emConflito.has(f.id) || !f.revServidor)
  const citados = new Set(fichas.map((f) => f.produtor?.id))
  const produtores = e.produtores.filter((p) => e.pendentes.produtores.includes(p.id) || p.id in e.recusadas.produtores
    || citados.has(p.id))
  estado = { ...e, fichas, produtores, ultimaSync: null }
  try { localStorage.setItem(chave, JSON.stringify(estado)) } catch { semDisco = true }
  ouvintes.forEach((f) => f())
}

export function substituirTudo(e: Estado): void {
  mudar((x) => Object.assign(x, completar(e)))
}

/* ---------- fichas ---------- */

/**
 * GM-2026-0001: sigla da pessoa, ano, sequência. Com a sigla na frente, dois
 * aparelhos sem sinal nunca geram o mesmo número.
 */
export function proximoNumero(e: Estado, data: string): string {
  const ano = (data || hoje()).slice(0, 4)
  e.seq[ano] = (e.seq[ano] ?? 0) + 1
  const n = `${ano}-${String(e.seq[ano]).padStart(4, '0')}`
  return dono?.sigla ? `${dono.sigla}-${n}` : n
}

/** Depois de baixar do servidor: a sequência local nunca fica atrás dos números já usados. */
export function acertarSequencia(e: Estado): void {
  const sigla = dono?.sigla
  for (const f of e.fichas) {
    const m = sigla ? f.numero.match(new RegExp(`^${sigla}-(\\d{4})-(\\d+)$`)) : f.numero.match(/^(\d{4})-(\d+)$/)
    if (!m) continue
    const ano = m[1]!, n = Number(m[2])
    if (n > (e.seq[ano] ?? 0)) e.seq[ano] = n
  }
}

export function acharFicha(e: Estado, id: string): Ficha | undefined {
  return e.fichas.find((f) => f.id === id)
}

/** Altera uma ficha e marca a hora da alteração (é isso que a torna pendente). */
export function mudarFicha(id: string, fn: (f: Ficha, e: Estado) => void): void {
  mudar((e) => {
    const f = acharFicha(e, id)
    if (!f) return
    fn(f, e)
    f.alteradaEm = agora()
  })
}

/* ---------- produtores ---------- */

export function acharProdutorPeloNome(e: Estado, nome: string): Produtor | undefined {
  const n = normalizar(nome)
  return n ? e.produtores.find((p) => p.ativo !== false && normalizar(p.nome) === n) : undefined
}

export function produtorVazio(): Produtor {
  return { id: '', nome: '', doc: '', ie: '', propriedade: '', endereco: '', municipio: '', uf: '', telefone: '', gps: null, alteradoEm: '' }
}

/**
 * Grava o produtor no cadastro (novo ou existente) e devolve a versão gravada.
 * Achado só pelo nome com outra grafia: vale a grafia do cadastro.
 */
export function guardarProdutor(e: Estado, d: Produtor): Produtor {
  let p = (d.id && e.produtores.find((x) => x.id === d.id)) || acharProdutorPeloNome(e, d.nome)
  if (!p) {
    p = { ...produtorVazio(), id: novoId() }
    e.produtores.push(p)
  } else if (normalizar(p.nome) === normalizar(d.nome)) {
    d = { ...d, nome: p.nome }
  }
  Object.assign(p, { ...d, id: p.id, nome: d.nome.trim(), uf: d.uf.trim().toUpperCase(), alteradoEm: agora() })
  return { ...p }
}
